import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// HubSpot Logo component
const HubSpotLogo = () => (
  <img
    src="https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png"
    alt="HubSpot"
    style={{ width: 40, height: 40, marginRight: '1rem' }}
    onError={(e) => {
      e.target.style.display = 'none';
    }}
  />
);

// Direction icons
const DirectionIcon = ({ direction }) => {
  switch (direction) {
    case 'bidirectional':
      return <span style={{ fontSize: '1.25rem' }}>⇄</span>;
    case 'to_hubspot':
      return <span style={{ fontSize: '1.25rem' }}>→</span>;
    case 'from_hubspot':
      return <span style={{ fontSize: '1.25rem' }}>←</span>;
    default:
      return <span style={{ fontSize: '1.25rem' }}>⇄</span>;
  }
};

// ============================================================================
// Field Mapping Editor
// ============================================================================

const FieldMappingEditor = ({ mappings, availableFields, hubspotProperties, onSave, onCancel }) => {
  const [localMappings, setLocalMappings] = useState(mappings || []);
  const [newMapping, setNewMapping] = useState({
    salesradar: '',
    hubspot: '',
    direction: 'bidirectional'
  });

  const directionOptions = [
    { value: 'bidirectional', label: 'Bidirectional' },
    { value: 'to_hubspot', label: 'To HubSpot Only' },
    { value: 'from_hubspot', label: 'From HubSpot Only' }
  ];

  const handleAddMapping = () => {
    if (newMapping.salesradar && newMapping.hubspot) {
      setLocalMappings([...localMappings, { ...newMapping }]);
      setNewMapping({ salesradar: '', hubspot: '', direction: 'bidirectional' });
    }
  };

  const handleRemoveMapping = (index) => {
    const updated = localMappings.filter((_, i) => i !== index);
    setLocalMappings(updated);
  };

  const handleUpdateMapping = (index, field, value) => {
    const updated = [...localMappings];
    updated[index][field] = value;
    setLocalMappings(updated);
  };

  const tableHeaderStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--gray-200)'
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid var(--gray-100)',
    verticalAlign: 'middle'
  };

  const selectStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: 'var(--gray-50)' }}>
            <tr>
              <th style={tableHeaderStyle}>Sales Radar Field</th>
              <th style={tableHeaderStyle}>Direction</th>
              <th style={tableHeaderStyle}>HubSpot Property</th>
              <th style={{ ...tableHeaderStyle, width: 50 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {localMappings.map((mapping, index) => (
              <tr key={index}>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.salesradar}
                    onChange={(e) => handleUpdateMapping(index, 'salesradar', e.target.value)}
                    style={selectStyle}
                  >
                    {availableFields.map(field => (
                      <option key={field} value={field}>{field}</option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.direction}
                    onChange={(e) => handleUpdateMapping(index, 'direction', e.target.value)}
                    style={selectStyle}
                  >
                    {directionOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.hubspot}
                    onChange={(e) => handleUpdateMapping(index, 'hubspot', e.target.value)}
                    style={selectStyle}
                  >
                    {hubspotProperties.map(prop => (
                      <option key={prop.name} value={prop.name}>
                        {prop.label} ({prop.name})
                      </option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <button
                    onClick={() => handleRemoveMapping(index)}
                    disabled={mapping.required}
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: 'none',
                      backgroundColor: mapping.required ? 'var(--gray-100)' : 'var(--red-50)',
                      color: mapping.required ? 'var(--gray-400)' : 'var(--red-600)',
                      borderRadius: '0.25rem',
                      cursor: mapping.required ? 'not-allowed' : 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {/* Add new mapping row */}
            <tr style={{ backgroundColor: 'var(--gray-50)' }}>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.salesradar}
                  onChange={(e) => setNewMapping({ ...newMapping, salesradar: e.target.value })}
                  style={selectStyle}
                >
                  <option value="" disabled>Select field...</option>
                  {availableFields.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.direction}
                  onChange={(e) => setNewMapping({ ...newMapping, direction: e.target.value })}
                  style={selectStyle}
                >
                  {directionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.hubspot}
                  onChange={(e) => setNewMapping({ ...newMapping, hubspot: e.target.value })}
                  style={selectStyle}
                >
                  <option value="" disabled>Select property...</option>
                  {hubspotProperties.map(prop => (
                    <option key={prop.name} value={prop.name}>{prop.label}</option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <button
                  onClick={handleAddMapping}
                  disabled={!newMapping.salesradar || !newMapping.hubspot}
                  className="btn btn-primary"
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '1rem',
                    opacity: (!newMapping.salesradar || !newMapping.hubspot) ? 0.5 : 1
                  }}
                >
                  ➕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={() => onSave(localMappings)}>
          Save Mappings
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Conflict Resolution Modal
// ============================================================================

const ConflictResolutionModal = ({ conflict, onClose, onResolve }) => {
  const [resolution, setResolution] = useState('salesradar_wins');
  const [loading, setLoading] = useState(false);

  const handleResolve = async () => {
    setLoading(true);
    await onResolve(conflict.id, resolution);
    setLoading(false);
    onClose();
  };

  if (!conflict) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ 
        width: '100%', 
        maxWidth: '500px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        margin: '1rem'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
            Resolve Sync Conflict
          </h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--yellow-50)',
            border: '1px solid var(--yellow-200)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>⚠️</span>
            <span style={{ color: 'var(--yellow-800)', fontSize: '0.875rem' }}>
              This record has conflicting data between Sales Radar and HubSpot.
            </span>
          </div>
          
          <p style={{ fontWeight: 500, marginBottom: '1rem', color: 'var(--gray-700)' }}>
            Lead: {conflict.lead_email}
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              border: '1px solid var(--gray-200)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                Sales Radar
              </span>
              <p style={{ fontWeight: 500, margin: '0.25rem 0' }}>
                {conflict.lead_data?.first_name} {conflict.lead_data?.last_name}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: '0.25rem 0' }}>
                {conflict.lead_data?.company}
              </p>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                Updated: {conflict.lead_data?.updated_at}
              </span>
            </div>
            <div style={{
              padding: '1rem',
              border: '1px solid var(--gray-200)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                HubSpot
              </span>
              <p style={{ fontWeight: 500, margin: '0.25rem 0' }}>
                Contact ID: {conflict.hubspot_id}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: '0.25rem 0' }}>
                {conflict.error}
              </p>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Resolution Strategy
            </label>
            <select
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="salesradar_wins">Sales Radar Wins - Overwrite HubSpot</option>
              <option value="hubspot_wins">HubSpot Wins - Overwrite Sales Radar</option>
            </select>
          </div>
        </div>
        <div style={{ 
          padding: '1rem 1.5rem', 
          borderTop: '1px solid var(--gray-200)', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '0.5rem' 
        }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={handleResolve}
            disabled={loading}
          >
            {loading ? 'Resolving...' : 'Resolve'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Toast Notification
// ============================================================================

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'error' ? 'var(--red-600)' : 'var(--green-600)';

  return (
    <div style={{
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      backgroundColor: bgColor,
      color: 'white',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      zIndex: 1001
    }}>
      <span>{type === 'error' ? '❌' : '✅'}</span>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          marginLeft: '0.5rem',
          background: 'none',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '1.25rem'
        }}
      >
        ×
      </button>
    </div>
  );
};

// ============================================================================
// Main HubSpot Integration Component
// ============================================================================

const HubSpotIntegration = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [fieldMappings, setFieldMappings] = useState(null);
  const [hubspotProperties, setHubspotProperties] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingMappings, setEditingMappings] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [statusRes, settingsRes, mappingsRes, historyRes, conflictsRes] = await Promise.all([
        api.get('/api/crm/hubspot/status'),
        api.get('/api/crm/hubspot/settings'),
        api.get('/api/crm/hubspot/field-mappings'),
        api.get('/api/crm/hubspot/sync-history'),
        api.get('/api/crm/hubspot/conflicts')
      ]);
      
      setStatus(statusRes.data);
      setSettings(settingsRes.data.settings);
      setFieldMappings(mappingsRes.data.mappings);
      setSyncHistory(historyRes.data.history || []);
      setConflicts(conflictsRes.data.conflicts || []);
      
      // Fetch HubSpot properties if connected
      if (statusRes.data.connected) {
        try {
          const propsRes = await api.get('/api/crm/hubspot/properties?type=contacts');
          setHubspotProperties(propsRes.data.properties || []);
        } catch (e) {
          console.error('Failed to fetch HubSpot properties:', e);
        }
      }
      
    } catch (error) {
      console.error('Error fetching HubSpot data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Connect to HubSpot
  const handleConnect = async () => {
    try {
      const response = await api.get('/api/crm/hubspot/connect');
      if (response.data.oauth_url) {
        window.location.href = response.data.oauth_url;
      }
    } catch (error) {
      showToast('Failed to initiate HubSpot connection', 'error');
    }
  };

  // Disconnect from HubSpot
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect HubSpot?')) return;
    
    try {
      await api.post('/api/crm/hubspot/disconnect');
      setStatus({ connected: false });
      showToast('HubSpot disconnected successfully');
    } catch (error) {
      showToast('Failed to disconnect HubSpot', 'error');
    }
  };

  // Trigger sync
  const handleSync = async (direction = 'bidirectional', type = 'contacts') => {
    setSyncing(true);
    try {
      await api.post('/api/crm/hubspot/sync', { direction, type });
      showToast('Sync completed successfully');
      fetchData();
    } catch (error) {
      showToast('Sync failed: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Update settings
  const handleUpdateSettings = async (newSettings) => {
    try {
      await api.put('/api/crm/hubspot/settings', newSettings);
      setSettings({ ...settings, ...newSettings });
      showToast('Settings updated');
    } catch (error) {
      showToast('Failed to update settings', 'error');
    }
  };

  // Save field mappings
  const handleSaveMappings = async (mappings) => {
    try {
      await api.put('/api/crm/hubspot/field-mappings', {
        mappings: { contacts: mappings }
      });
      setFieldMappings({ ...fieldMappings, contacts: mappings });
      setEditingMappings(false);
      showToast('Field mappings saved');
    } catch (error) {
      showToast('Failed to save mappings', 'error');
    }
  };

  // Resolve conflict
  const handleResolveConflict = async (conflictId, resolution) => {
    try {
      await api.post(`/api/crm/hubspot/conflicts/${conflictId}/resolve`, { resolution });
      setConflicts(conflicts.filter(c => c.id !== conflictId));
      showToast('Conflict resolved');
    } catch (error) {
      showToast('Failed to resolve conflict', 'error');
    }
  };

  const tabs = [
    { label: 'Settings', icon: '⚙️' },
    { label: 'Field Mapping', icon: '🔄' },
    { label: 'Sync History', icon: '📋' },
    { label: `Conflicts${conflicts.length > 0 ? ` (${conflicts.length})` : ''}`, icon: '⚠️' }
  ];

  const tableHeaderStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--gray-200)'
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid var(--gray-100)'
  };

  const selectStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: 'var(--gray-600)' }}>Loading HubSpot integration...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
        <HubSpotLogo />
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--gray-900)', margin: 0 }}>
            HubSpot Integration
          </h1>
          <p style={{ color: 'var(--gray-600)', margin: '0.25rem 0 0 0' }}>
            Sync contacts, deals, and activities with HubSpot CRM
          </p>
        </div>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          borderRadius: '9999px',
          fontSize: '0.875rem',
          fontWeight: 500,
          backgroundColor: status?.connected ? 'var(--green-100)' : 'var(--gray-100)',
          color: status?.connected ? 'var(--green-700)' : 'var(--gray-700)'
        }}>
          {status?.connected ? '✅ Connected' : '⚪ Disconnected'}
        </span>
      </div>

      {/* Connection Card - Not Connected */}
      {!status?.connected ? (
        <div className="card" style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
            Connect HubSpot
          </h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
            Connect your HubSpot account to enable bi-directional sync of contacts, 
            deals, and activities between Sales Radar and HubSpot CRM.
          </p>
          <div style={{ marginBottom: '1.5rem' }}>
            {[
              { icon: '✓', title: 'Automatic contact sync', desc: 'Keep contacts in sync between both platforms' },
              { icon: '✓', title: 'Deal synchronization', desc: 'Track deals and opportunities' },
              { icon: '✓', title: 'Activity timeline', desc: 'Push Sales Radar events to HubSpot timeline' },
              { icon: '✓', title: 'Lead scoring', desc: 'Sync Sales Radar scores to HubSpot' }
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <span style={{ color: 'var(--green-600)', fontWeight: 'bold' }}>{item.icon}</span>
                <div>
                  <p style={{ fontWeight: 500, margin: 0 }}>{item.title}</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="btn btn-primary" onClick={handleConnect} style={{ padding: '0.75rem 1.5rem' }}>
            🔗 Connect HubSpot
          </button>
        </div>
      ) : (
        <>
          {/* Sync Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Contacts Synced', value: status?.sync_stats?.contacts_synced || 0 },
              { label: 'Deals Synced', value: status?.sync_stats?.deals_synced || 0 },
              { label: 'Pending Sync', value: status?.sync_stats?.pending_sync || 0 },
              { label: 'Last Sync', value: status?.sync_stats?.last_sync ? new Date(status.sync_stats.last_sync).toLocaleString() : 'Never', isText: true }
            ].map((stat, idx) => (
              <div key={idx} className="card" style={{ padding: '1.5rem' }}>
                <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', margin: 0 }}>{stat.label}</p>
                <p style={{ 
                  fontSize: stat.isText ? '0.875rem' : '1.5rem', 
                  fontWeight: 600, 
                  color: 'var(--gray-900)', 
                  margin: '0.5rem 0 0 0' 
                }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {/* Sync Actions */}
          <div className="card" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, fontWeight: 500 }}>Quick Actions</span>
              <button
                className="btn btn-primary"
                onClick={() => handleSync('bidirectional', 'all')}
                disabled={syncing}
              >
                {syncing ? '⏳ Syncing...' : '🔄 Full Sync'}
              </button>
              <button
                className="btn"
                onClick={() => handleSync('to_hubspot', 'contacts')}
                disabled={syncing}
              >
                → Push to HubSpot
              </button>
              <button
                className="btn"
                onClick={() => handleSync('from_hubspot', 'contacts')}
                disabled={syncing}
              >
                ← Pull from HubSpot
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDisconnect}
              >
                🔌 Disconnect
              </button>
            </div>
            {syncing && (
              <div style={{ 
                marginTop: '1rem',
                height: '4px',
                backgroundColor: 'var(--gray-200)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: '30%',
                  backgroundColor: 'var(--primary-600)',
                  animation: 'progress 1s ease-in-out infinite'
                }} />
              </div>
            )}
          </div>

          {/* Conflicts Alert */}
          {conflicts.length > 0 && (
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--yellow-50)',
              border: '1px solid var(--yellow-200)',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ color: 'var(--yellow-800)' }}>
                ⚠️ {conflicts.length} sync conflict(s) need resolution
              </span>
              <button
                className="btn btn-link"
                onClick={() => setActiveTab(3)}
                style={{ color: 'var(--yellow-700)' }}
              >
                View Conflicts
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              display: 'flex', 
              borderBottom: '1px solid var(--gray-200)',
              overflowX: 'auto'
            }}>
              {tabs.map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  style={{
                    padding: '1rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: activeTab === idx ? 'var(--primary-600)' : 'var(--gray-600)',
                    borderBottom: activeTab === idx ? '2px solid var(--primary-600)' : '2px solid transparent',
                    fontWeight: activeTab === idx ? 500 : 400,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Settings Tab */}
          {activeTab === 0 && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1.5rem' }}>
                Sync Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {/* Auto Sync Toggle */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings?.auto_sync_enabled || false}
                      onChange={(e) => handleUpdateSettings({ auto_sync_enabled: e.target.checked })}
                      style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }}
                    />
                    <span>Enable automatic sync</span>
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem', marginLeft: '2rem' }}>
                    Automatically sync changes between platforms
                  </p>
                </div>

                {/* Sync Frequency */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Sync Frequency
                  </label>
                  <select
                    value={settings?.sync_frequency || 'hourly'}
                    onChange={(e) => handleUpdateSettings({ sync_frequency: e.target.value })}
                    disabled={!settings?.auto_sync_enabled}
                    style={selectStyle}
                  >
                    <option value="realtime">Real-time</option>
                    <option value="15min">Every 15 minutes</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                  </select>
                </div>

                {/* Sync Direction */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Sync Direction
                  </label>
                  <select
                    value={settings?.sync_direction || 'bidirectional'}
                    onChange={(e) => handleUpdateSettings({ sync_direction: e.target.value })}
                    style={selectStyle}
                  >
                    <option value="bidirectional">Bidirectional</option>
                    <option value="to_hubspot">To HubSpot Only</option>
                    <option value="from_hubspot">From HubSpot Only</option>
                  </select>
                </div>

                {/* Conflict Resolution */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Conflict Resolution
                  </label>
                  <select
                    value={settings?.conflict_resolution || 'most_recent_wins'}
                    onChange={(e) => handleUpdateSettings({ conflict_resolution: e.target.value })}
                    style={selectStyle}
                  >
                    <option value="most_recent_wins">Most Recent Wins</option>
                    <option value="salesradar_wins">Sales Radar Wins</option>
                    <option value="hubspot_wins">HubSpot Wins</option>
                    <option value="manual">Manual Resolution</option>
                  </select>
                </div>
              </div>

              <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid var(--gray-200)' }} />
              
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>
                Additional Options
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                {[
                  { key: 'sync_new_leads', label: 'Sync new leads automatically' },
                  { key: 'sync_score_updates', label: 'Sync score updates' },
                  { key: 'create_deals_for_mql', label: 'Create deals for MQLs' },
                  { key: 'webhook_enabled', label: 'Enable webhooks' }
                ].map((option) => (
                  <label key={option.key} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings?.[option.key] || false}
                      onChange={(e) => handleUpdateSettings({ [option.key]: e.target.checked })}
                      style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Field Mapping Tab */}
          {activeTab === 1 && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, flex: 1, margin: 0 }}>
                  Contact Field Mappings
                </h3>
                {!editingMappings && (
                  <button className="btn" onClick={() => setEditingMappings(true)}>
                    ✏️ Edit Mappings
                  </button>
                )}
              </div>
              
              {editingMappings ? (
                <FieldMappingEditor
                  mappings={fieldMappings?.contacts || []}
                  availableFields={fieldMappings?.available_salesradar_fields?.contacts || []}
                  hubspotProperties={hubspotProperties}
                  onSave={handleSaveMappings}
                  onCancel={() => setEditingMappings(false)}
                />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: 'var(--gray-50)' }}>
                      <tr>
                        <th style={tableHeaderStyle}>Sales Radar Field</th>
                        <th style={tableHeaderStyle}>Direction</th>
                        <th style={tableHeaderStyle}>HubSpot Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fieldMappings?.contacts || []).map((mapping, idx) => (
                        <tr key={idx}>
                          <td style={tableCellStyle}>
                            {mapping.salesradar}
                            {mapping.required && (
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.125rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: 'var(--gray-100)',
                                borderRadius: '9999px'
                              }}>
                                Required
                              </span>
                            )}
                          </td>
                          <td style={tableCellStyle}>
                            <DirectionIcon direction={mapping.direction} />
                          </td>
                          <td style={tableCellStyle}>{mapping.hubspot}</td>
                        </tr>
                      ))}
                      {(!fieldMappings?.contacts || fieldMappings.contacts.length === 0) && (
                        <tr>
                          <td colSpan={3} style={{ ...tableCellStyle, textAlign: 'center', color: 'var(--gray-500)' }}>
                            No field mappings configured
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Sync History Tab */}
          {activeTab === 2 && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, flex: 1, margin: 0 }}>
                  Sync History
                </h3>
                <button className="btn" onClick={fetchData}>
                  🔄 Refresh
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: 'var(--gray-50)' }}>
                    <tr>
                      <th style={tableHeaderStyle}>Lead Email</th>
                      <th style={tableHeaderStyle}>HubSpot ID</th>
                      <th style={tableHeaderStyle}>Direction</th>
                      <th style={tableHeaderStyle}>Status</th>
                      <th style={tableHeaderStyle}>Last Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map((item, idx) => (
                      <tr key={idx}>
                        <td style={tableCellStyle}>{item.local_email || `ID: ${item.local_id}`}</td>
                        <td style={tableCellStyle}>{item.external_id}</td>
                        <td style={tableCellStyle}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: 'var(--gray-100)',
                            borderRadius: '0.25rem'
                          }}>
                            <DirectionIcon direction={item.direction} />
                            {item.direction}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            borderRadius: '0.25rem',
                            backgroundColor: item.status === 'synced' ? 'var(--green-100)' : item.status === 'error' ? 'var(--red-100)' : 'var(--gray-100)',
                            color: item.status === 'synced' ? 'var(--green-700)' : item.status === 'error' ? 'var(--red-700)' : 'var(--gray-700)'
                          }}>
                            {item.status === 'synced' ? '✅' : item.status === 'error' ? '❌' : '⏳'} {item.status}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          {item.last_sync ? new Date(item.last_sync).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                    {syncHistory.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ ...tableCellStyle, textAlign: 'center', color: 'var(--gray-500)' }}>
                          No sync history yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Conflicts Tab */}
          {activeTab === 3 && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1.5rem' }}>
                Sync Conflicts
              </h3>
              {conflicts.length === 0 ? (
                <div style={{
                  padding: '1rem',
                  backgroundColor: 'var(--green-50)',
                  border: '1px solid var(--green-200)',
                  borderRadius: '0.5rem',
                  color: 'var(--green-700)'
                }}>
                  ✅ No conflicts to resolve. All records are in sync!
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: 'var(--gray-50)' }}>
                      <tr>
                        <th style={tableHeaderStyle}>Lead</th>
                        <th style={tableHeaderStyle}>HubSpot ID</th>
                        <th style={tableHeaderStyle}>Issue</th>
                        <th style={tableHeaderStyle}>Created</th>
                        <th style={tableHeaderStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conflicts.map((conflict) => (
                        <tr key={conflict.id}>
                          <td style={tableCellStyle}>{conflict.lead_email}</td>
                          <td style={tableCellStyle}>{conflict.hubspot_id}</td>
                          <td style={tableCellStyle}>{conflict.error}</td>
                          <td style={tableCellStyle}>
                            {new Date(conflict.created_at).toLocaleString()}
                          </td>
                          <td style={tableCellStyle}>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                              onClick={() => setSelectedConflict(conflict)}
                            >
                              Resolve
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Conflict Resolution Modal */}
      {selectedConflict && (
        <ConflictResolutionModal
          conflict={selectedConflict}
          onClose={() => setSelectedConflict(null)}
          onResolve={handleResolveConflict}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default HubSpotIntegration;
